import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import {
  Visibility as ViewIcon,
  DeleteSweep as CleanupIcon,
  Refresh as RefreshIcon,
} from '@material-ui/icons';
import { useStyles } from './style';

interface TestRunListProps {
  testRuns: any[];
  loading: boolean;
  cleaningUp: boolean;
  onViewRun: (runId: string) => void;
  onCleanup: (programId: string, runId: string) => void;
  onRefresh: () => void;
}

const TestRunList: React.FC<TestRunListProps> = ({
  testRuns,
  loading,
  cleaningUp,
  onViewRun,
  onCleanup,
  onRefresh,
}) => {
  const classes = useStyles();

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      pending: classes.statusPending,
      running: classes.statusRunning,
      success: classes.statusSuccess,
      error: classes.statusError,
      cleaning: classes.statusCleaning,
      cleaned: classes.statusCleaned,
    };
    return map[status] || '';
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getDuration = (start: string, end: string) => {
    if (!start || !end) return '-';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Run ID</TableCell>
            <TableCell>Test ID</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Prefix</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell align="right">
              <Tooltip title="Refresh">
                <IconButton size="small" onClick={onRefresh} disabled={loading}>
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {testRuns.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                No test runs found
              </TableCell>
            </TableRow>
          )}
          {testRuns.map((run: any) => (
            <TableRow key={run.runId || run._id}>
              <TableCell style={{ fontFamily: 'monospace', fontSize: 13 }}>
                {run.runId}
              </TableCell>
              <TableCell>{run.testId}</TableCell>
              <TableCell>
                <Chip
                  label={run.status}
                  size="small"
                  className={`${classes.statusChip} ${getStatusClass(run.status)}`}
                />
              </TableCell>
              <TableCell style={{ fontFamily: 'monospace' }}>{run.prefix}</TableCell>
              <TableCell>{formatDate(run.startedAt)}</TableCell>
              <TableCell>{getDuration(run.startedAt, run.completedAt)}</TableCell>
              <TableCell align="right">
                <Tooltip title="View Details">
                  <IconButton size="small" onClick={() => onViewRun(run.runId || run._id)}>
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
                {run.programId && run.status !== 'cleaned' && (
                  <Tooltip title="Cleanup">
                    <IconButton
                      size="small"
                      onClick={() => onCleanup(run.programId, run.runId)}
                      disabled={cleaningUp}
                    >
                      <CleanupIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TestRunList;
